import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/Popover'
import { shiftYear, useComparePeriod } from '../hooks/useComparePeriod'

interface Props {
  planId: string
  // Plan window — "Last year" shifts it back one year.
  beginDate: string
  endDate: string
  // What the server compared with on this render.
  start: string
  end: string
  // Page path + partial-reload keys for applying a new window.
  path: string
  only: string[]
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

// Compact compare-period control: plain "Comparing: …" text that opens a
// popover with the date range editor and a last-year shortcut. The choice is
// per viewer (URL + localStorage), never written to the plan.
export default function PeriodPicker({ planId, beginDate, endDate, start, end, path, only }: Props) {
  const [open, setOpen] = useState(false)
  const [localStart, setLocalStart] = useState(start)
  const [localEnd, setLocalEnd] = useState(end)
  const { apply: applyPeriod } = useComparePeriod({ planId, path, only, current: { start, end } })

  useEffect(() => { setLocalStart(start) }, [start])
  useEffect(() => { setLocalEnd(end) }, [end])

  const apply = (nextStart: string, nextEnd: string) => {
    setOpen(false)
    applyPeriod({ start: nextStart, end: nextEnd })
  }

  const applyLastYear = () => apply(shiftYear(beginDate, -1), shiftYear(endDate, -1))

  const dirty = localStart !== start || localEnd !== end

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="group rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
          Comparing:{' '}
          <span className="font-medium text-foreground">
            {formatDate(start)} – {formatDate(end)}
          </span>
          <span aria-hidden className="ml-1 text-muted-foreground group-hover:text-foreground">▾</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-3">
        <div className="mb-2 flex items-center justify-between gap-6">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Compare with past period
          </span>
          <button
            type="button"
            onClick={applyLastYear}
            className="text-xs underline hover:no-underline"
            style={{ color: 'var(--link)' }}
          >
            Last year
          </button>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={localStart}
            onChange={(e) => setLocalStart(e.target.value)}
            className="h-8 w-auto"
          />
          <span className="text-sm text-muted-foreground">to</span>
          <Input
            type="date"
            value={localEnd}
            onChange={(e) => setLocalEnd(e.target.value)}
            className="h-8 w-auto"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => apply(localStart, localEnd)}
            disabled={!dirty}
          >
            Go
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
